import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getDisplayName, getDisplayNamesForUrls } from "@/lib/documentMetadata";

/**
 * Hook to get the display name for a single document URL.
 * Uses React Query for caching and automatic updates.
 */
export function useDocumentDisplayName(url: string | null | undefined) {
  const { data: displayName, isLoading } = useQuery({
    queryKey: ['document-display-name', url],
    queryFn: async () => {
      if (!url) return null;
      return await getDisplayName(url);
    },
    enabled: !!url,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return {
    displayName: displayName || null,
    isLoading,
  };
}

/**
 * Hook to get display names for multiple document URLs.
 * More efficient than calling useDocumentDisplayName for each URL.
 */
export function useDocumentDisplayNames(urls: (string | null | undefined)[]) {
  const validUrls = urls.filter((url): url is string => !!url);
  
  const { data: displayNames, isLoading } = useQuery({
    queryKey: ['document-display-names', ...validUrls.sort()],
    queryFn: async () => {
      if (validUrls.length === 0) return new Map<string, string>();
      return await getDisplayNamesForUrls(validUrls);
    },
    enabled: validUrls.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const getDisplayNameForUrl = (url: string | null | undefined): string => {
    if (!url) return 'Dokument';
    return displayNames?.get(url) || url.split('/').pop()?.split('?')[0] || 'Dokument';
  };

  return {
    displayNames: displayNames || new Map<string, string>(),
    getDisplayNameForUrl,
    isLoading,
  };
}

/**
 * Hook to invalidate document display name cache.
 * Call this after renaming a document.
 */
export function useInvalidateDocumentDisplayNames() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['document-display-name'] });
    queryClient.invalidateQueries({ queryKey: ['document-display-names'] });
  };

  const invalidateUrl = (url: string) => {
    queryClient.invalidateQueries({ queryKey: ['document-display-name', url] });
    queryClient.invalidateQueries({ queryKey: ['document-display-names'] });
  };

  return {
    invalidateAll,
    invalidateUrl,
  };
}
